import {
  ChartBackground,
  ChartData,
  ChartKind,
  ChartSeries,
  SlideObject,
  createObjectId,
  defaultLocks,
} from '../types';

export const CHART_KINDS: ChartKind[] = ['column', 'bar', 'line', 'pie'];
export const CHART_MAX_CATEGORIES = 12;
export const CHART_MAX_SERIES = 6;
export const CHART_DEFAULT_CATEGORIES = ['Q1', 'Q2', 'Q3', 'Q4'];
export const CHART_DEFAULT_VALUES = [4, 6, 5, 7];
export const CHART_SERIES_COLOR_IDS = ['olympia', 'originalet', 'hetta', 'hav', 'gras', 'bougainvillea'];
export const CHART_TEXT = '#111111';
export const CHART_GRID = '#998A6B';
export const CHART_BG = '#FFFFFF';
export const CHART_SAND_BG = '#EDE8DC';
export const CHART_FONT_SIZE = 14;
export const CHART_HEX: Record<string, string> = {
  olympia: '#1B3967',
  originalet: '#0050E6',
  hetta: '#D94000',
  hav: '#4DCCA6',
  gras: '#9FC300',
  bougainvillea: '#9933B3',
  ekologisk: '#33BF00',
  sand: '#998A6B',
  'sand-ljus': '#EDE8DC',
  'sand-extra-ljus': '#F5F0E8',
  vit: '#FFFFFF',
  svart: '#111111',
};

export function parseChartNumber(raw?: string | number): number {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : 0;
  const text = String(raw || '').trim().replace(/\s/g, '').replace(',', '.');
  const value = Number(text);
  return Number.isFinite(value) ? value : 0;
}

export function normalizeChartKind(kind?: string | null): ChartKind {
  if (kind === 'bar' || kind === 'line' || kind === 'pie') return kind;
  return 'column';
}

export function defaultSeriesColorId(index: number, ids?: string[]): string {
  const list = ids?.length ? ids : CHART_SERIES_COLOR_IDS;
  return list[index % list.length] || 'olympia';
}

export function normalizeChartBackground(value?: string | null): ChartBackground {
  if (value === 'white' || value === 'sand') return value;
  return 'none';
}

export function padCategoryColorIds(ids: string[] | undefined, count: number, colorIds?: string[]): string[] {
  return Array.from({ length: Math.max(1, count) }, (_, index) => (
    cleanLabel(ids?.[index], defaultSeriesColorId(index, colorIds)) || defaultSeriesColorId(index, colorIds)
  ));
}

function clampCount(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, Math.floor(value) || min));
}

function cleanLabel(value?: string, fallback = ''): string {
  return String(value || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim() || fallback;
}

function createSeries(index: number, input?: Partial<ChartSeries>, categoryCount = 4, colorIds?: string[]): ChartSeries {
  const values = Array.from({ length: categoryCount }, (__, valueIndex) => {
    const raw = input?.values?.[valueIndex];
    return raw == null ? (index === 0 ? CHART_DEFAULT_VALUES[valueIndex] ?? 0 : 0) : parseChartNumber(raw);
  });
  return {
    id: input?.id || createObjectId('series'),
    name: cleanLabel(input?.name, `Serie ${index + 1}`),
    colorId: cleanLabel(input?.colorId, defaultSeriesColorId(index, colorIds)) || 'olympia',
    values,
  };
}

export function createDefaultChart(input?: Partial<ChartData> & { colorIds?: string[] }): ChartData {
  const categories = (input?.categories?.length ? input.categories : CHART_DEFAULT_CATEGORIES)
    .slice(0, CHART_MAX_CATEGORIES)
    .map((label, index) => cleanLabel(label, `Q${index + 1}`));
  const count = Math.max(1, categories.length);
  const seriesCount = clampCount(input?.series?.length || 1, 1, CHART_MAX_SERIES);
  const resolved = categories.length ? categories : [...CHART_DEFAULT_CATEGORIES];
  return {
    kind: normalizeChartKind(input?.kind),
    categories: resolved,
    series: Array.from({ length: seriesCount }, (_, index) => (
      createSeries(index, input?.series?.[index], resolved.length, input?.colorIds)
    )),
    showLegend: input?.showLegend !== false,
    showValues: Boolean(input?.showValues),
    background: normalizeChartBackground(input?.background),
    categoryColorIds: padCategoryColorIds(input?.categoryColorIds, resolved.length, input?.colorIds),
  };
}

export function normalizeChart(chart?: ChartData | null): ChartData {
  if (!chart || !Array.isArray(chart.categories) || !chart.categories.length || !Array.isArray(chart.series) || !chart.series.length) {
    return createDefaultChart(chart || undefined);
  }
  const categories = chart.categories
    .slice(0, CHART_MAX_CATEGORIES)
    .map((label, index) => cleanLabel(label, `Q${index + 1}`));
  const count = Math.max(1, categories.length);
  const series = chart.series.slice(0, CHART_MAX_SERIES).map((item, index) => createSeries(index, item, count));
  const resolved = categories.length ? categories : [...CHART_DEFAULT_CATEGORIES];
  return {
    kind: normalizeChartKind(chart.kind),
    categories: resolved,
    series: series.length ? series : [createSeries(0, undefined, resolved.length)],
    showLegend: chart.showLegend !== false,
    showValues: Boolean(chart.showValues),
    background: normalizeChartBackground(chart.background),
    categoryColorIds: padCategoryColorIds(chart.categoryColorIds, resolved.length),
  };
}

export function createDefaultChartObject(input: {
  z: number;
  x?: number;
  y?: number;
  w?: number;
  h?: number;
  kind?: ChartKind;
  colorIds?: string[];
  showLegend?: boolean;
  showValues?: boolean;
  background?: ChartBackground;
}): SlideObject {
  return {
    id: createObjectId('chart'),
    type: 'chart',
    x: input.x ?? 160,
    y: input.y ?? 180,
    w: input.w ?? 960,
    h: input.h ?? 400,
    z: input.z,
    locks: defaultLocks(),
    chart: createDefaultChart({
      kind: input.kind,
      colorIds: input.colorIds,
      showLegend: input.showLegend,
      showValues: input.showValues,
      background: input.background,
    }),
  };
}

export function chartFromGrid(grid: string[][], existing?: ChartData | null): { chart: ChartData; truncated: boolean } {
  const source = normalizeChart(existing);
  if (!grid.length) return { chart: source, truncated: false };
  const truncated = grid.length - 1 > CHART_MAX_SERIES || Math.max(0, ...grid.map((row) => row.length)) - 1 > CHART_MAX_CATEGORIES;
  const first = grid[0] || [];
  const firstRowNumeric = first.every((cell) => {
    const label = cleanLabel(cell);
    return !label || /^-?\d/.test(label);
  });
  const hasHeader = first.length > 1 && !firstRowNumeric;
  const rawCategories = hasHeader ? first.slice(1) : first;
  const dataRows = hasHeader ? grid.slice(1) : grid;
  const categories = rawCategories
    .slice(0, CHART_MAX_CATEGORIES)
    .map((label, index) => cleanLabel(label, `Q${index + 1}`));
  const count = Math.max(1, categories.length);
  const rows = dataRows.slice(0, CHART_MAX_SERIES);
  const series = (rows.length ? rows : [['Serie 1']]).map((row, index) => {
    const named = hasHeader || (cleanLabel(row[0]) && !/^-?\d/.test(cleanLabel(row[0])));
    const name = named ? cleanLabel(row[0], `Serie ${index + 1}`) : `Serie ${index + 1}`;
    const cells = named ? row.slice(1) : row;
    return createSeries(index, {
      id: source.series[index]?.id,
      name,
      colorId: source.series[index]?.colorId,
      values: Array.from({ length: count }, (__, valueIndex) => parseChartNumber(cells[valueIndex])),
    }, count);
  });
  return {
    chart: {
      kind: source.kind,
      categories: categories.length ? categories : [...CHART_DEFAULT_CATEGORIES],
      series,
      showLegend: source.showLegend,
      showValues: source.showValues,
      background: source.background,
      categoryColorIds: padCategoryColorIds(source.categoryColorIds, (categories.length ? categories : CHART_DEFAULT_CATEGORIES).length),
    },
    truncated,
  };
}

export function resizeChartCategories(chart: ChartData | undefined, count: number): ChartData {
  const current = normalizeChart(chart);
  const next = clampCount(count, 1, CHART_MAX_CATEGORIES);
  return {
    ...current,
    categories: Array.from({ length: next }, (_, index) => current.categories[index] || `Q${index + 1}`),
    categoryColorIds: padCategoryColorIds(current.categoryColorIds, next),
    series: current.series.map((item, index) => createSeries(index, {
      ...item,
      values: Array.from({ length: next }, (__, valueIndex) => item.values[valueIndex] ?? 0),
    }, next)),
  };
}

export function resizeChartSeries(chart: ChartData | undefined, count: number): ChartData {
  const current = normalizeChart(chart);
  const next = clampCount(count, 1, CHART_MAX_SERIES);
  return {
    ...current,
    series: Array.from({ length: next }, (_, index) => createSeries(index, current.series[index], current.categories.length)),
  };
}

export function setChartKind(chart: ChartData | undefined, kind: ChartKind): ChartData {
  return { ...normalizeChart(chart), kind: normalizeChartKind(kind) };
}

export function mergeChartData(layoutChart: ChartData | undefined, slideChart: ChartData | undefined): ChartData {
  const layout = normalizeChart(layoutChart);
  const slide = normalizeChart(slideChart);
  const sameGrid = layout.categories.length === slide.categories.length && layout.series.length === slide.series.length;
  return {
    kind: layout.kind,
    showLegend: layout.showLegend,
    showValues: layout.showValues,
    background: layout.background,
    categoryColorIds: layout.categoryColorIds,
    categories: sameGrid ? slide.categories : layout.categories,
    series: layout.series.map((item, index) => ({
      ...item,
      name: sameGrid ? (slide.series[index]?.name || item.name) : item.name,
      values: sameGrid ? (slide.series[index]?.values || item.values) : item.values,
    })),
  };
}

export function cloneChartData(chart: ChartData | undefined): ChartData {
  const current = normalizeChart(chart);
  return {
    ...current,
    categories: [...current.categories],
    categoryColorIds: [...current.categoryColorIds],
    series: current.series.map((item) => ({
      ...item,
      id: createObjectId('series'),
      values: [...item.values],
    })),
  };
}

export function sanitizeChartObject(object: SlideObject): SlideObject {
  if (object.type !== 'chart') return object;
  return { ...object, chart: normalizeChart(object.chart) };
}

export function chartSearchText(object: Pick<SlideObject, 'type' | 'chart'>): string {
  if (object.type !== 'chart') return '';
  const chart = normalizeChart(object.chart);
  return [
    ...chart.categories,
    ...chart.series.flatMap((item) => [item.name, ...item.values.map((value) => String(value))]),
  ].join(' ').toLowerCase();
}

export function chartContentSignature(chart?: ChartData): string {
  const current = normalizeChart(chart);
  return JSON.stringify({
    categories: current.categories,
    series: current.series.map((item) => ({ name: item.name, values: item.values })),
  });
}

export function chartStyleSignature(chart?: ChartData): string {
  const current = normalizeChart(chart);
  return JSON.stringify({
    kind: current.kind,
    showLegend: current.showLegend,
    showValues: current.showValues,
    background: current.background,
    colors: current.series.map((item) => item.colorId),
    categoryColorIds: current.categoryColorIds,
  });
}

export function isChartObject(object?: Pick<SlideObject, 'type'> | null): object is SlideObject {
  return object?.type === 'chart';
}

export function chartHasPlottableValues(chart?: ChartData): boolean {
  const current = normalizeChart(chart);
  if (current.kind === 'pie') {
    return current.series[0]?.values.some((value) => value > 0) || false;
  }
  return current.series.some((item) => item.values.some((value) => value !== 0));
}
